import { FC } from 'react';
import styled from 'styled-components';
import { TopDecorColor } from '../specific_ppv_year_info_component/single_match_card_in_info_ppv_folder/GenericComponentCssMatchCard';

const ContainerCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  color: white;
  width: 100%;
  min-height: 110px;
  border-radius: 10px;
  overflow: hidden;
  padding: 20px 20px 16px;
  gap: 16px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: rgba(163, 27, 27, 0.5);
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.3);
    transform: translateY(-2px);
  }
`;

const Position = styled.div<{ $position: number }>`
  font-size: 22px;
  font-weight: 800;
  min-width: 36px;
  text-align: center;
  flex-shrink: 0;
  color: ${({ $position }) =>
    $position === 1 ? 'gold' :
    $position === 2 ? '#c0c0c0' :
    $position === 3 ? '#cd7f32' :
    'rgba(255,255,255,0.3)'};
`;

const AvatarNameSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 80px;
  gap: 6px;

  img {
    width: 72px;
    height: 72px;
    object-fit: cover;
    object-position: top;
    border-radius: 6px;
    border: 2px solid rgba(163, 27, 27, 0.4);
    cursor: pointer;
    transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: rgba(163, 27, 27, 0.85);
      transform: scale(1.06);
      box-shadow: 0 0 10px rgba(163, 27, 27, 0.25);
    }
  }

  h6 {
    margin: 0;
    text-transform: uppercase;
    text-align: center;
    font-size: 10px;
    letter-spacing: 0.5px;
    line-height: 1.3;
    color: rgba(255, 255, 255, 0.75);
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StatsSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;

  .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;

    h5 {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      margin: 0;
      color: rgba(255, 255, 255, 0.4);
    }

    .value-fights {
      font-size: 22px;
      font-weight: 800;
      margin: 0;
      color: rgba(255, 255, 255, 0.75);
    }

    .value-wins {
      font-size: 22px;
      font-weight: 800;
      margin: 0;
      color: rgba(255, 215, 0, 0.9);
    }
  }
`;

const BoxInfoBeltsFightsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  min-height: 60px;

  & > h5 {
    margin: 0 0 6px 0;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: rgba(255, 255, 255, 0.35);
  }

  .box-belts-fights {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;

    .box-single-belt-fight {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 3px 8px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      gap: 5px;

      img {
        width: 24px;
        height: 24px;
        object-fit: cover;
      }

      h5.count-belts {
        font-size: 11px;
        margin: 0;
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }
`;

const ScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 215, 0, 0.06);
  border-radius: 8px;
  padding: 8px 14px;
  border: 1px solid rgba(255, 215, 0, 0.2);
  min-width: 70px;

  .score-label {
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    margin: 0 0 2px 0;
    color: rgba(255, 215, 0, 0.5);
  }

  .score-value {
    font-size: 20px;
    font-weight: 800;
    margin: 0;
    color: rgba(255, 215, 0, 0.9);
  }
`;

interface WrestlerStats {
  position: number;
  avatar_path: string | null;
  name: string;
  fights_count: number;
  wins_count: number;
  importance_score: string | number;
}

interface BeltFight {
  img_path: string;
  belt_name: string;
  count: number;
  wins: number;
}

interface BestWrestlerCardProps {
  wrestlerStats: WrestlerStats;
  beltFights: BeltFight[];
  navigateToWrestler: (wrestler: WrestlerStats) => void;
}

const BestWrestlerCard: FC<BestWrestlerCardProps> = ({ wrestlerStats, beltFights, navigateToWrestler }) => {
  return (
    <ContainerCard className="container-best-wrestler-card">
      <TopDecorColor />

      <Position $position={wrestlerStats.position}>
        {wrestlerStats.position}
      </Position>

      <AvatarNameSection>
        <img
          src={wrestlerStats.avatar_path ? process.env.REACT_APP_STORAGE_URL + wrestlerStats.avatar_path : undefined}
          alt={wrestlerStats.name}
          title={wrestlerStats.name}
          onClick={() => navigateToWrestler(wrestlerStats)}
        />
        <h6>{wrestlerStats.name}</h6>
      </AvatarNameSection>

      <StatsSection>
        <div className="stat-item">
          <h5>Fights</h5>
          <h6 className="value-fights">{wrestlerStats.fights_count}</h6>
        </div>
        <div className="stat-item">
          <h5>Wins</h5>
          <h6 className="value-wins">{wrestlerStats.wins_count}</h6>
        </div>
      </StatsSection>

      <BoxInfoBeltsFightsContainer className="box-info-belts-fights">
        <h5>Incontri titolati</h5>
        <div className="box-belts-fights">
          {beltFights.map((belt, key) => (
            <div className="box-single-belt-fight" key={key}>
              <img
                src={process.env.REACT_APP_STORAGE_URL + belt.img_path}
                alt={belt.img_path + "_alt"}
                title={belt.belt_name}
              />
              <h5 className="count-belts">x{belt.count} ({belt.wins})</h5>
            </div>
          ))}
        </div>
      </BoxInfoBeltsFightsContainer>

      <ScoreContainer>
        <p className="score-label">Score</p>
        <p className="score-value">{parseFloat(String(wrestlerStats.importance_score)).toFixed(2)}</p>
      </ScoreContainer>
    </ContainerCard>
  );
};

export default BestWrestlerCard;
